import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation, Trans } from 'react-i18next';
import type { FieldErrors, UseFormMethods } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { tokens, Button, Link, Text, Icon } from '@hotjar/hotsauce';
import { css } from '@emotion/react';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useSsoSignIn, FetchStatus } from '@hotjar/auth';
import { useUserLogin } from '@hotjar/auth/src/domains/authentication/useUserLogin';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { TopBar } from '@hotjar/webapp/features/Activation/TopBar';

import type { SignInSubmitDataModel } from '@hotjar/auth';
import Illustration from '@hotjar/shared-assets/images/scale/sso/sso-illustration.svg';
import { FadeIn } from '@hotjar/webapp/utils/animations';

import { useNavigate } from 'react-router-dom';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { getSalesLink } from '@hotjar/upsell';
import { SignInForm, SsoSignInStep } from './SignInForm';

interface PageProps extends Partial<Pick<UseFormMethods, 'register'>> {
  handleBackClick: () => void;
  formSubmit: (e: React.BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors;
  status: FetchStatus;
  step: SsoSignInStep;
  redirectToLogin: () => void;
  apiErrorMessage: string | null;
  isSubmitting: boolean;
}
const TABLET_BREAKPOINT = '(min-width: 768px)';
const LEARNMORE_LINK = '/articles/1500005375602';

const Row = css`
  animation: ${FadeIn} 0.3s ease;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 100%;

  @media ${TABLET_BREAKPOINT} {
    flex-direction: row;
  }
`;

const Column = css`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  flex: 1;
`;

const Container = styled.div`
  ${Row}
`;

const ColumnForm = styled.div`
  ${Column}
  padding: ${tokens.spacer.x2} ${tokens.spacer.x2} 0 ${tokens.spacer.x2};

  @media ${TABLET_BREAKPOINT} {
    max-width: 500px;
    padding: ${tokens.spacer.x5} ${tokens.spacer.x4} 0 ${tokens.spacer.x4};
  }
`;

const ColumnSso = styled.div`
  ${Column}
  background-color: ${tokens.color.bg_active};
  margin: 0 ${tokens.spacer.x2} ${tokens.spacer.x2};
  border-radius: ${tokens.radius.l};

  @media ${TABLET_BREAKPOINT} {
    display: flex;
    flex: 3;
    min-height: calc(100vh - 54px);
    text-align: center;
    justify-content: center;
    align-items: center;
    border-radius: unset;
    margin: unset;
    position: relative;
  }
`;

const AboutSsoContanier = styled.div`
  margin: ${tokens.spacer.x2};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  padding: ${tokens.spacer.x2};

  @media ${TABLET_BREAKPOINT} {
    max-width: 736px;
    margin-top: unset;
    align-items: center;
  }
`;

const SsoIllustration = styled.img`
  height: 60px;
  width: 60px;
  padding-bottom: ${tokens.spacer.x2};

  @media ${TABLET_BREAKPOINT} {
    height: 250px;
    width: 300px;
  }
`;

const BackButton = styled(Button)`
  left: 0;
  position: absolute;
  color: ${tokens.color.text_default};
  ${tokens.text('subtitle2')};
`;

const FooterCta = styled.div`
  margin: ${tokens.spacer.x4} 0;
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};

  @media ${TABLET_BREAKPOINT} {
    margin: ${tokens.spacer.x6} 0;
  }
`;

const Title = styled(Text)`
  margin-bottom: ${tokens.spacer.x1};
  text-align: left;

  @media ${TABLET_BREAKPOINT} {
    text-align: center;
  }
`;

const Description = styled(Text)`
  margin: 0 0 ${tokens.spacer.x3};
  text-align: justify;

  @media ${TABLET_BREAKPOINT} {
    text-align: center;
  }
`;

const Page = ({
  handleBackClick,
  formSubmit,
  errors,
  register,
  status,
  step,
  redirectToLogin,
  apiErrorMessage,
  isSubmitting,
}: PageProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { click } = useTrack();

  const track = (eventName: string) => {
    click(eventName, 'ssoSignIn', {
      url_event_was_triggered_on: '/login/sso',
    });
  };

  const handleLearnMoreClick = () => {
    track('Login/SSO Learn More');
    window.open(`${helpHotjarUrl}${LEARNMORE_LINK}`, '_blank', 'noopener noreferrer');
  };

  const handleContactUsClick = () => {
    track('Login/SSO Contact us');
    window.open(getSalesLink('SsoSignup'), '_blank', 'noopener noreferrer');
  };

  const isDisabled: boolean =
    isSubmitting || status === FetchStatus.SUCCESS || status === FetchStatus.FETCHING;

  return (
    <>
      <TopBar>
        <BackButton kind="ghost" onClick={handleBackClick} size="m" icon="chevron_left">
          {t('activation:saml-sso.signin.go-back')}
        </BackButton>
      </TopBar>
      <Container>
        <ColumnForm>
          <SignInForm
            onSubmit={formSubmit}
            errors={errors}
            register={register}
            isDisabled={isDisabled}
            step={step}
            apiErrorMessage={apiErrorMessage}
          />
          <FooterCta>
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <Icon
                icon="chevron_left"
                css={css`
                  margin-right: ${tokens.spacer.x0};
                `}
              />
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link as="button" kind="standalone" onClick={redirectToLogin}>
                {t('activation:saml-sso.signin.go-back-email')}
              </Link>
            </div>
          </FooterCta>
        </ColumnForm>
        <ColumnSso>
          <AboutSsoContanier>
            <SsoIllustration src={Illustration} alt={t('common:sso.page-title')} />
            <Title textType="header5" as="h5">
              {t('common:sso.cta-banner.scale.title')}
            </Title>
            <Trans i18nKey="common:sso.about-sso">
              <Description textType="body1" as="p">
                Single sign-on (SSO) is an authentication type that lets team members access Hotjar
                through an identity provider. This feature is only available on the Hotjar Scale
                plan. Contact us to upgrade your site to Hotjar Scale or
                <Link kind="inline" as="button" onClick={handleLearnMoreClick}>
                  learn more about SSO
                </Link>
                at our knowledge base.
              </Description>
            </Trans>
            <Button kind="secondary" onClick={handleContactUsClick}>
              {t('common:sso.contact-us')}
            </Button>
          </AboutSsoContanier>
        </ColumnSso>
      </Container>
    </>
  );
};

export function SsoSignInPage(): JSX.Element {
  const [step, setStep] = useState<SsoSignInStep>(SsoSignInStep.Email);
  const [email, setEmail] = useState<string>('');
  const navigate = useNavigate();

  const { fieldErrors, nextUrl, status, loginWithSso, apiErrorMessage } = useSsoSignIn();

  const { logoutAndClearData } = useUserLogin();

  const { register, handleSubmit, errors, setError, formState } = useForm<SignInSubmitDataModel>({
    mode: 'onBlur',
  });
  const { isSubmitting } = formState;
  const onSubmit = (values: SignInSubmitDataModel) => {
    if (step === SsoSignInStep.Email) {
      setEmail(values.email);
      setStep(SsoSignInStep.LoginId);
      return;
    }
    logoutAndClearData(() => loginWithSso(email, values.loginId));
  };

  const redirectToLogin = () => {
    navigate(LOGIN.path);
  };

  const formSubmit = handleSubmit(onSubmit);
  const handleBackClick = () => {
    if (step === SsoSignInStep.LoginId) {
      setStep(SsoSignInStep.Email);
      return;
    }
    redirectToLogin();
  };

  useEffect(() => {
    if (fieldErrors?.email) {
      // If we have an email error, go back to the email step
      // so we can show the email error on the email field.
      setStep(SsoSignInStep.Email);
      // Clear values so we can try again
      setEmail('');
      // Set the error from the server on the email field
      setError('email', {
        type: 'manual',
        message: fieldErrors.email[0],
      });
    } else if (fieldErrors?.loginId) {
      // If we have a loginId error (validation error or top-level error)
      // we ensure we are in the login step and set the error message to show on the
      // loginId field
      setStep(SsoSignInStep.LoginId);
      setError('loginId', {
        type: 'manual',
        message: fieldErrors.loginId[0],
      });
    }
  }, [fieldErrors?.email, fieldErrors?.loginId, setError, setStep]);

  useEffect(() => {
    if (nextUrl) {
      window.location.href = nextUrl;
    }
  }, [nextUrl]);

  return (
    <Page
      handleBackClick={handleBackClick}
      formSubmit={formSubmit}
      errors={errors}
      register={register}
      status={status}
      step={step}
      redirectToLogin={redirectToLogin}
      apiErrorMessage={apiErrorMessage}
      isSubmitting={isSubmitting}
    />
  );
}
